import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { CustomDashboardPinService } from './custom-dashboard-pin.service';
import { DashboardLayoutService } from './dashboard-layout.service';

/**
 * Minimum shape of an editor draft row that the Customize Layout popup
 * produces. Each engine-based dashboard component (Monthly Report 1 / 2,
 * etc.) already builds rows with this exact shape — see
 * `LayoutEditorItem` in those components — so they can be passed into
 * {@link CustomizeLayoutBatchService.applyEditorDraft} directly without
 * any per-component mapping.
 *
 * Only the fields needed to build the
 * `POST /dashboards/:id/widgets/batch` payload are required; consumer
 * row types may carry additional fields (`title`, `id`, `placementId`,
 * etc.) which are ignored by the service.
 */
export interface CustomizeLayoutBatchDraftItem {
  /** Catalog `widgets.id` already resolved by the engine. */
  widgetId: number;
  /** Backend `pages.id` the widget originated from. */
  sourcePageId: number | null;
  /** 4 | 6 | 8 | 12 — width inside the 12-col grid. */
  colSpan: number;
  /** Whether the card is shown on the live grid. */
  visible: boolean;
  /** Forwarded verbatim as `widgets[].custom_config`. */
  customConfig: Record<string, any> | null;
  /** Ascending position used to sort the payload. */
  order: number;
}

/**
 * Params for {@link CustomizeLayoutBatchService.applyEditorDraft}.
 */
export interface ApplyCustomizeLayoutParams {
  /**
   * Backend `dashboards.id` for the user's personalized dashboard on
   * the current page. When `null`/`undefined` no request is fired
   * (mirrors the existing `if (dashId && allDraft.length)` guard in
   * the report components).
   */
  dashboardId: number | null | undefined;
  /**
   * Source page id used as the fallback when an editor row's
   * `sourcePageId` is `null` (e.g. brand-new widgets that have not
   * been persisted yet). Typically `dashboardEngineConfig.pageId`.
   */
  fallbackSourcePageId: number | string;
  /** Editor draft rows from the Customize Layout popup. */
  draft: ReadonlyArray<CustomizeLayoutBatchDraftItem>;
}

/**
 * Params for {@link CustomizeLayoutBatchService.applyEditorDraftToPageDashboard}.
 *
 * Used by report pages that are NOT built on `DashboardPageEngine`
 * (Social Platform / SEO Stats / etc.) — those pages don't carry a
 * `dashboardId` signal in their state, so the service first probes
 * the user's per-page personalized dashboard and uses the resolved
 * id for the batch update. When no per-page dashboard exists yet
 * (first-time customization), the service bootstraps one via
 * `createDashboard({ type: 'product_page', page_id })` — same
 * pattern the engine itself uses in `_applyDashboardVisibilitySelection`.
 */
export interface ApplyCustomizeLayoutToPageParams {
  /** Catalog `pages.id` for the report page (e.g. Social Platform). */
  pageId: number;
  /** Active product id (e.g. from `resolveActiveCatalogProductId`). */
  productId: string | number | null;
  /**
   * Source page id used as the fallback when a draft row's
   * `sourcePageId` is `null`. Defaults to `pageId` when omitted.
   */
  fallbackSourcePageId?: number | string;
  /** Editor draft rows from the Customize Layout popup. */
  draft: ReadonlyArray<CustomizeLayoutBatchDraftItem>;
}

/**
 * Editor draft row keyed by `widget_key` instead of pre-resolved
 * `widgetId`. Used by report pages whose section ids ARE the
 * canonical `widget_key` (SEO Stats / SEO Overview / Campaign Ads
 * Stats / Industry / Training / Lendee / Cool Credit / Practina /
 * Lendee Analytics / Growth Activation), so they can hand the
 * editor draft directly to the service without first resolving
 * each section's numeric `widgets.id`.
 */
export interface CustomizeLayoutBatchDraftItemByKey {
  /** Backend `widget_key` (or canonical section id). */
  key: string;
  /** Backend `pages.id` the widget originated from. */
  sourcePageId?: number | null;
  /** 4 | 6 | 8 | 12 — width inside the 12-col grid. */
  colSpan: number;
  /** Whether the card is shown on the live grid. */
  visible: boolean;
  /** Forwarded verbatim as `widgets[].custom_config`. */
  customConfig?: Record<string, any> | null;
  /** Ascending position used to sort the payload. */
  order: number;
}

/**
 * Params for
 * {@link CustomizeLayoutBatchService.applyEditorDraftToPageDashboardByKeys}.
 *
 * Companion of {@link ApplyCustomizeLayoutToPageParams} — same
 * lookup-then-batch-or-bootstrap flow against the user's per-page
 * personalized dashboard, but the draft rows carry `widget_key`s
 * instead of pre-resolved numeric `widgetId`s. The service resolves
 * each key against the catalog (via
 * {@link CustomDashboardPinService.resolveCatalogWidgetIdsForPage})
 * before assembling the batch payload, so each report component
 * just hands its `editorItems()` straight in.
 */
export interface ApplyCustomizeLayoutToPageByKeysParams {
  /** Catalog `pages.id` for the report page. */
  pageId: number;
  /** Active product id (e.g. from `resolveActiveCatalogProductId`). */
  productId: string | number | null;
  /**
   * Source page id used as the fallback when a draft row's
   * `sourcePageId` is `null`. Defaults to `pageId` when omitted.
   */
  fallbackSourcePageId?: number | string;
  /** Editor draft rows from the Customize Layout popup. */
  draft: ReadonlyArray<CustomizeLayoutBatchDraftItemByKey>;
}

/**
 * Centralizes the "Apply" batch update that every engine-based
 * Customize Layout popup fires.
 *
 * Before this service, every component duplicated the same payload
 * builder + `batchUpdateWidgets` call + `loadPersonalizedDashboardLayout()`
 * refresh. Components now just inject this service and call
 * {@link applyEditorDraft}; the success callback is left to the caller
 * so each page can refresh its own layout (engine-based pages call
 * `loadPersonalizedDashboardLayout()`).
 *
 * The service is a thin wrapper — it does not change error/toast
 * behaviour vs. the inlined version: errors are silently swallowed
 * (mirrors `catchError(() => of(null))` in the original components),
 * giving the caller a stable Observable that always completes with
 * `null` on failure.
 */
@Injectable({ providedIn: 'root' })
export class CustomizeLayoutBatchService {
  private readonly layoutService = inject(DashboardLayoutService);
  private readonly customDashboardPin = inject(CustomDashboardPinService);

  /**
   * Builds the batch payload from `draft` and posts it to the
   * `POST /dashboards/:id/widgets/batch` endpoint.
   *
   * Behaviour matches the previous inline implementation in Monthly
   * Report 1 / 2:
   *   • Rows whose `widgetId <= 0` (i.e. unresolved catalog ids) are
   *     dropped — they cannot round-trip to the backend.
   *   • Remaining rows are sorted by ascending `order` so the server
   *     receives the widgets in the user-visible order.
   *   • ALL widgets are sent (visible AND hidden) with their
   *     `is_visible` flag — visibility is controlled exclusively via
   *     this flag, so the server has the full layout snapshot.
   *
   * Returns an Observable that emits `null` (and completes) when no
   * request needed to be fired (no dashboard id, or no eligible
   * rows). Otherwise it emits the backend's
   * {@link DashboardLoadResponse} on success or `null` on failure.
   */
  applyEditorDraft(params: ApplyCustomizeLayoutParams): Observable<unknown> {
    const { dashboardId, fallbackSourcePageId, draft } = params;

    if (!dashboardId) return of(null);

    const sortedDraft = draft
      .filter((d) => d.widgetId > 0)
      .slice()
      .sort((a, b) => a.order - b.order);

    if (!sortedDraft.length) return of(null);

    return this.layoutService
      .batchUpdateWidgets(dashboardId, {
        widgets: sortedDraft.map((d) => ({
          widget_id: String(d.widgetId),
          source_page_id: String(d.sourcePageId ?? fallbackSourcePageId),
          col_span: d.colSpan,
          row_span: 1,
          is_visible: d.visible,
          custom_config: d.customConfig ?? {},
        })),
      })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Convenience wrapper that fires {@link applyEditorDraft} and
   * subscribes immediately, invoking `onComplete` once the request
   * finishes (success or silent failure).
   *
   * Replaces the boilerplate
   * ```
   * this._layoutService.batchUpdateWidgets(...)
   *   .pipe(catchError(() => of(null)))
   *   .subscribe(() => this.loadPersonalizedDashboardLayout());
   * ```
   * with a single call. Returns `true` when a request was actually
   * fired so callers can branch on whether `onComplete` is async.
   */
  applyEditorDraftAndRefresh(
    params: ApplyCustomizeLayoutParams,
    onComplete?: () => void,
  ): boolean {
    const fired =
      !!params.dashboardId &&
      params.draft.some((d) => d.widgetId > 0);

    this.applyEditorDraft(params)
      .pipe(tap(() => onComplete?.()))
      .subscribe();

    return fired;
  }

  /**
   * Resolves the user's per-page personalized dashboard, then fires
   * the batch update against it. Mirrors the exact dashboard target
   * Monthly Report 1 / 2 hit when their inline Customize Layout
   * popup is applied, so non-engine report pages now persist
   * customizations to the same dashboard the engine pages do.
   *
   * Flow:
   *   1. `GET /dashboards?page_id=<pageId>&mixed=false[&product_id]`
   *      probes the per-page personalized dashboard.
   *   2. If one exists → `POST /dashboards/:id/widgets/batch` with
   *      the full draft snapshot (visible + hidden, controlled by
   *      `is_visible`).
   *   3. If none exists yet (first-time customization for this user
   *      / page) → bootstrap via
   *      `POST /dashboards { type:'product_page', page_id, widget_ids }`
   *      with the visible draft rows. Subsequent Applys take the
   *      step-2 fast path against the freshly created dashboard.
   *      This bootstrap mirrors the engine's
   *      `_applyDashboardVisibilitySelection` flow, so engine and
   *      non-engine pages converge on the same backend shape.
   *   4. Errors are silently swallowed (matches MR1/MR2 behaviour).
   *
   * `onComplete` runs once the chain finishes regardless of which
   * branch was taken, so callers can refresh local state.
   */
  applyEditorDraftToPageDashboard(
    params: ApplyCustomizeLayoutToPageParams,
    onComplete?: () => void,
  ): void {
    const { pageId, productId, fallbackSourcePageId, draft } = params;

    if (!draft.length) {
      onComplete?.();
      return;
    }

    this.layoutService
      .load(pageId, 'web', false, productId)
      .pipe(
        catchError(() => of(null as any)),
        switchMap((res: any) =>
          this._applyOrBootstrap(res, {
            pageId,
            fallbackSourcePageId: fallbackSourcePageId ?? pageId,
            draft,
          }),
        ),
        tap(() => onComplete?.()),
      )
      .subscribe();
  }

  /**
   * Same flow as {@link applyEditorDraftToPageDashboard} but accepts
   * draft rows keyed by `widget_key`. The service resolves each key
   * against the catalog (via
   * {@link CustomDashboardPinService.resolveCatalogWidgetIdsForPage})
   * so callers don't need to maintain a `sectionId → widgets.id` map.
   *
   * Frontend section ids that have no backend catalog counterpart
   * are silently dropped (never sent in the payload). This mirrors
   * the existing pin-button gate (`canPinToCustomDashboard`) and
   * means it's always safe to hand the entire editor draft straight
   * in — the service filters non-pinnable sections internally.
   *
   * When no per-page dashboard exists yet, this bootstrap-creates one
   * via `createDashboard({ type:'product_page', page_id, widget_ids })`,
   * mirroring the engine's
   * `_applyDashboardVisibilitySelection` fallback so the very first
   * Apply on a page successfully persists to backend (rather than
   * silently no-op-ing).
   */
  applyEditorDraftToPageDashboardByKeys(
    params: ApplyCustomizeLayoutToPageByKeysParams,
    onComplete?: () => void,
  ): void {
    const { pageId, productId, fallbackSourcePageId, draft } = params;

    if (!draft.length) {
      onComplete?.();
      return;
    }

    // ── Resolve `widget_key → widgets.id` from BOTH sources in
    // parallel so the chain is robust to either being empty:
    //
    //   • Catalog (`resolveCatalogWidgetIdsForPage`) — primary
    //     source, used for bootstrap when no dashboard exists yet.
    //   • Existing per-page dashboard (`load(..., mixed=false)`) —
    //     mines `widget_id` ↔ `widget_key` directly from the user's
    //     own dashboard payload, so pages whose catalog entries are
    //     missing or out-of-sync still resolve correctly.
    //
    // This also guarantees the `GET /dashboards` request fires
    // unconditionally (visible in network), instead of bailing
    // silently before any HTTP call when the catalog map is empty.
    forkJoin({
      catalogIds: this.customDashboardPin
        .resolveCatalogWidgetIdsForPage(pageId, productId)
        .pipe(catchError(() => of(new Map<string, number>()))),
      loadRes: this.layoutService
        .load(pageId, 'web', false, productId)
        .pipe(catchError(() => of(null as any))),
    })
      .pipe(
        switchMap(({ catalogIds, loadRes }) => {
          const idMap = this._mergeWidgetIdSources(catalogIds, loadRes);

          const resolvedDraft: CustomizeLayoutBatchDraftItem[] = draft
            .map((d) => ({
              widgetId: idMap.get(d.key) ?? 0,
              sourcePageId: d.sourcePageId ?? pageId,
              colSpan: d.colSpan,
              visible: d.visible,
              customConfig: d.customConfig ?? null,
              order: d.order,
            }))
            .filter((d) => d.widgetId > 0);

          if (!resolvedDraft.length) {
            // eslint-disable-next-line no-console
            console.warn(
              `[CustomizeLayoutBatchService] No widget_keys from the editor ` +
                `draft could be resolved against either the catalog or the ` +
                `existing per-page dashboard for pageId=${pageId}. ` +
                `Verify the page is registered in the dashboards catalog ` +
                `with widget_keys matching the front-end section ids: ` +
                `${draft.map((d) => d.key).join(', ')}`,
            );
            return of(null);
          }

          return this._applyOrBootstrap(loadRes, {
            pageId,
            fallbackSourcePageId: fallbackSourcePageId ?? pageId,
            draft: resolvedDraft,
          });
        }),
        tap(() => onComplete?.()),
      )
      .subscribe();
  }

  /**
   * Combines two `widget_key → widgets.id` sources into a single
   * lookup map used by
   * {@link applyEditorDraftToPageDashboardByKeys}:
   *
   *   • `catalogIds` — what `resolveCatalogWidgetIdsForPage` produced
   *     (canonical + raw keys).
   *   • The user's existing per-page dashboard (`loadRes.widgets`),
   *     which carries the canonical `widget_key` and numeric
   *     `widget_id` directly. Mining this also handles the case
   *     where the catalog has no widget entries for the page yet
   *     (common for newer pages) — the pre-existing dashboard
   *     widgets become the source of truth.
   *
   * Catalog entries take precedence; dashboard widgets only fill in
   * keys the catalog didn't already cover, so we never overwrite a
   * known-good catalog id with a stale dashboard id.
   */
  private _mergeWidgetIdSources(
    catalogIds: Map<string, number>,
    loadRes: any,
  ): Map<string, number> {
    const idMap = new Map<string, number>(catalogIds);

    for (const w of loadRes?.widgets ?? []) {
      const id = Number(w?.widget_id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const raw = String(w?.widget_key ?? '');
      if (!raw) continue;
      if (!idMap.has(raw)) idMap.set(raw, id);
      // Also expose the canonical (suffix-stripped) variant so
      // front-end section ids that strip the product prefix still
      // match — same rule used by `resolveCatalogWidgetIdsForPage`.
      const canonical = this._stripProductPrefix(raw);
      if (canonical !== raw && !idMap.has(canonical)) {
        idMap.set(canonical, id);
      }
    }

    return idMap;
  }

  /**
   * Strips a leading 2-character product prefix (`de_`, `co_`,
   * `le_`, …) from a backend `widget_key` so frontend section
   * enums can be product-agnostic. Mirrors the helper in
   * `CustomDashboardPinService` — duplicated here to avoid
   * widening that service's public API for a single internal use.
   */
  private _stripProductPrefix(widgetKey: string): string {
    const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
    return match ? match[1] : widgetKey;
  }

  /**
   * Branches between batch-updating an existing per-page dashboard
   * and bootstrap-creating a brand-new one with the current draft
   * widgets. Shared by {@link applyEditorDraftToPageDashboard} and
   * {@link applyEditorDraftToPageDashboardByKeys} so both code paths
   * are guaranteed to produce identical backend behaviour.
   */
  private _applyOrBootstrap(
    loadRes: any,
    ctx: {
      pageId: number;
      fallbackSourcePageId: number | string;
      draft: ReadonlyArray<CustomizeLayoutBatchDraftItem>;
    },
  ): Observable<unknown> {
    const dashboardId =
      loadRes?.dashboard?.id != null ? Number(loadRes.dashboard.id) : null;

    if (dashboardId) {
      return this.applyEditorDraft({
        dashboardId,
        fallbackSourcePageId: ctx.fallbackSourcePageId,
        draft: ctx.draft,
      });
    }

    // ── Bootstrap path: no per-page dashboard exists yet for this
    // user. Seed one with the visible widgets only — `createDashboard`
    // does not accept `is_visible`, so hidden widgets are simply
    // omitted. They'll be added on the next Apply via the batch
    // upsert path. This mirrors what the engine does in
    // `_applyDashboardVisibilitySelection`.
    const visibleWidgets = ctx.draft
      .filter((d) => d.widgetId > 0 && d.visible)
      .slice()
      .sort((a, b) => a.order - b.order);

    if (!visibleWidgets.length) return of(null);

    return this.layoutService
      .createDashboard({
        name: 'My Dashboard',
        type: 'product_page',
        platform: 'web',
        page_id: ctx.pageId,
        widget_ids: visibleWidgets.map((d) => ({
          widget_id: d.widgetId,
          source_page_id:
            d.sourcePageId ?? Number(ctx.fallbackSourcePageId) ?? ctx.pageId,
          col_span: d.colSpan,
          row_span: 1,
          custom_config: d.customConfig ?? {},
        })),
      })
      .pipe(catchError(() => of(null)));
  }
}
